/**
 * Gated Content Block - Client-side functionality
 */

class GatedContent {
  constructor() {
    this.pendingPdfWindows = new WeakMap();
    this.init();
  }

  init() {
    // Inject hidden fields into CF7 forms
    this.injectHiddenFields();
    
    // Listen for CF7 form submissions
    document.addEventListener('wpcf7mailsent', this.handleFormSuccess.bind(this), false);

    // If CF7 fails (validation/spam/mail failed), close any pre-opened PDF tab
    document.addEventListener('wpcf7invalid', this.handleFormFailure.bind(this), false);
    document.addEventListener('wpcf7mailfailed', this.handleFormFailure.bind(this), false);
    document.addEventListener('wpcf7spam', this.handleFormFailure.bind(this), false);
    
    // Check for existing unlock cookie on page load
    this.handleTestingParams();
  }

  /**
   * Get post ID from body class (fallback method)
   */
  getPostIdFromBody() {
    const bodyClasses = document.body.className.split(' ');
    for (let className of bodyClasses) {
      if (className.startsWith('postid-')) {
        return className.replace('postid-', '');
      }
    }
    return null;
  }

  /**
   * Inject hidden fields into the CF7 form
   */
  injectHiddenFields() {
    const gatedForms = document.querySelectorAll('.gated-form');
    
    gatedForms.forEach(formContainer => {
      const form = formContainer.querySelector('form');
      if (!form) return;

      let postId = formContainer.dataset.postId;
      const blockId = formContainer.dataset.blockId;
      
      // Fallback: Get post ID from body class if not in dataset
      if (!postId || postId === '0') {
        postId = this.getPostIdFromBody();
      }
      
      const mode = formContainer.dataset.mode;
      const pdfUrl = formContainer.dataset.pdfUrl;
      const emailSubject = formContainer.dataset.emailSubject;
      const emailBody = formContainer.dataset.emailBody;

      // Popup-blocker-safe PDF opening: pre-open a tab on user submit gesture
      // (CF7 success event happens async and can be blocked otherwise)
      if (mode === 'send_pdf' && pdfUrl) {
        form.addEventListener('submit', () => {
          // Avoid re-opening multiple windows for the same form
          if (this.pendingPdfWindows.has(form)) {
            return;
          }

          const win = window.open('', '_blank');
          if (win && win.document) {
            try {
              win.document.title = 'Loading…';
            } catch (e) {
              // ignore
            }
          }

          this.pendingPdfWindows.set(form, win || null);
        });
      }

      // Create and append hidden fields
      const hiddenFields = [
        { name: 'gated_post_id', value: postId },
        { name: 'gated_block_id', value: blockId },
        { name: 'gated_mode', value: mode },
        { name: 'gated_pdf_url', value: pdfUrl },
        { name: 'gated_email_subject', value: emailSubject },
        { name: 'gated_email_body', value: emailBody }
      ];

      hiddenFields.forEach(field => {
        if (field.value && field.value !== '0') {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = field.name;
          input.value = field.value;
          form.appendChild(input);
        }
      });
    });
  }

  /**
   * Handle CF7 form success
   */
  handleFormSuccess(event) {
    const form = event.target;
    const formContainer = form.closest('.gated-form');
    if (!formContainer) return;

    const mode = formContainer.dataset.mode;
    let postId = formContainer.dataset.postId;
    const blockId = formContainer.dataset.blockId;
    
    // Fallback: Get post ID from body class if not in dataset
    if (!postId || postId === '0') {
      postId = this.getPostIdFromBody();
    }

    if (mode === 'reveal_below') {
      this.fetchGatedContent(postId, blockId);
    } else if (mode === 'send_pdf') {
      this.openPdfAfterSubmit(form, formContainer);
    }
  }

  /**
   * Handle CF7 failure states: close any pre-opened PDF tab.
   */
  handleFormFailure(event) {
    const form = event.target;
    if (!form) return;

    const win = this.pendingPdfWindows.get(form);
    if (win && !win.closed) {
      try {
        win.close();
      } catch (e) {
        // ignore
      }
    }
    this.pendingPdfWindows.delete(form);
  }

  /**
   * For send_pdf mode: open the PDF in a new tab/window.
   * Uses a pre-opened window (created on submit) to avoid popup blockers.
   */
  openPdfAfterSubmit(form, formContainer) {
    const pdfUrl = formContainer.dataset.pdfUrl;

    if (!pdfUrl) {
      this.showPdfOpenError(formContainer);
      this.pendingPdfWindows.delete(form);
      return;
    }

    const win = this.pendingPdfWindows.get(form);

    if (win && !win.closed) {
      try {
        win.location.href = pdfUrl;
      } catch (e) {
        window.open(pdfUrl, '_blank');
      }
    } else {
      window.open(pdfUrl, '_blank');
    }

    this.pendingPdfWindows.delete(form);
    this.showPdfOpenedMessage(formContainer);
  }

  /**
   * Fetch gated content from REST endpoint
   */
  async fetchGatedContent(postId, blockId) {
    try {
      if (!postId) {
        console.error('Cannot fetch gated content: postId is empty!');
        return;
      }
      
      const response = await fetch(`/wp-json/coingate/v1/gated-content/tail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          post_id: postId,
          block_id: blockId
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Inject gated content (idempotent: skip if container already has content)
        const container = document.querySelector(`[data-block-id="${blockId}"].gated-content`);
        if (container && data.content) {
          const hasContent = container.innerHTML && container.innerHTML.trim().length > 0;
          if (!hasContent) {
            container.innerHTML = data.content;
          }
        }

        // Set 30-day unlock cookies (client and server)
        this.setUnlockCookie(postId, blockId, 30);
        this.setServerAuthCookie(postId, blockId, 1); // 1 hour for server-side

        // Hide the form
        const formContainer = document.querySelector(`.gated-form[data-block-id="${blockId}"]`);
        if (formContainer) {
          formContainer.style.display = 'none';
        }

        // Hide outer wrapper (purple section) if marked with .hide-after-submit
        const outerWrapper = formContainer ? formContainer.closest('.hide-after-submit') : null;
        if (outerWrapper) {
          outerWrapper.style.display = 'none';
        }
      } else {
        const errorData = await response.json();
        console.error('REST API error:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error fetching gated content:', error);
    }
  }

  /**
   * Set server-side auth cookie (simulate PHP setcookie)
   */
  setServerAuthCookie(postId, blockId, hours) {
    const name = `gated_auth_${postId}_${blockId}`;
    const value = '1';
    const date = new Date();
    date.setTime(date.getTime() + (hours * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }



  /**
   * Show "PDF sent" message
   */
  showPdfOpenedMessage(formContainer) {
    const message = document.createElement('div');
    message.className = 'pdf-sent-message p-4 bg-green-100 text-green-800 rounded mt-4';
    message.innerHTML = '<strong>Success!</strong> Opening the PDF in a new tab.';
    
    formContainer.appendChild(message);
    
    // Hide form
    const form = formContainer.querySelector('form');
    if (form) {
      form.style.display = 'none';
    }

    // Also hide entire wrapper (purple block) after a short delay to let user see success message
    const outerWrapper = formContainer.closest('.hide-after-submit');
    if (outerWrapper) {
      setTimeout(() => {
        outerWrapper.style.display = 'none';
      }, 5000); // 5s delay so user can read the success message before the section disappears
    }
  }

  showPdfOpenError(formContainer) {
    const message = document.createElement('div');
    message.className = 'pdf-sent-message p-4 bg-red-100 text-red-800 rounded mt-4';
    message.innerHTML = '<strong>Error!</strong> PDF file is missing.';

    formContainer.appendChild(message);
  }

  /**
   * Handle testing URL parameters
   * ?gated_clear=1 -> remove all gated_* cookies
   * ?gated_ignore=1 -> skip auto unlock fetch
   * ?gated_force=1  -> force fetch reveal content regardless of cookies
   */
  handleTestingParams() {
    const params = new URLSearchParams(window.location.search);
    const shouldClear = params.get('gated_clear') === '1';
    const shouldIgnore = params.get('gated_ignore') === '1';
    const shouldForce = params.get('gated_force') === '1';

    if (shouldClear) {
      this.clearGatedCookies();
    }

    if (shouldIgnore) {
      return; // Do not auto-unlock
    }

    if (shouldForce) {
      this.forceUnlockFetch();
      return;
    }

    this.checkUnlockStatus();
  }

  /**
   * Clear all gated_* cookies
   */
  clearGatedCookies() {
    const cookies = document.cookie.split(';');
    cookies.forEach(c => {
      const name = c.split('=')[0].trim();
      if (name.startsWith('gated_auth_') || name.startsWith('gated_unlock_')) {
        document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
      }
    });
  }

  /**
   * Force fetch gated content for all reveal blocks regardless of cookies
   */
  forceUnlockFetch() {
    const containers = document.querySelectorAll('.gated-content[data-mode="reveal_below"]');
    containers.forEach(container => {
      let postId = container.dataset.postId || this.getPostIdFromBody() || this.findPostIdFromCookie(container.dataset.blockId);
      const blockId = container.dataset.blockId;
      if (postId && blockId) {
        this.fetchGatedContent(postId, blockId);
      }
    });
  }

  /**
   * Check if user has unlock cookie on page load
   */
  checkUnlockStatus() {
    // Only auto-fetch for containers the server marked as locked to avoid duplicate tail injection
    const gatedContainers = document.querySelectorAll('.gated-content[data-mode="reveal_below"][data-locked="1"]');
    
    gatedContainers.forEach(container => {
      let postId = container.dataset.postId;
      const blockId = container.dataset.blockId;

      // Fallback 1: derive postId from body classes
      if (!postId || postId === '0') {
        postId = this.getPostIdFromBody();
      }

      // Fallback 2: try to infer postId from existing unlock cookie names
      if ((!postId || postId === '0') && blockId) {
        const found = this.findPostIdFromCookie(blockId);
        if (found) postId = found;
      }

      if (postId && this.hasUnlockCookie(postId, blockId)) {
        // Auto-fetch gated content
        this.fetchGatedContent(postId, blockId);
      }
    });
  }

  /**
   * Try to find postId by scanning cookies for gated_unlock_*_<blockId>
   */
  findPostIdFromCookie(blockId) {
    const cookies = document.cookie.split(';');
    const suffix = `_${blockId}=`; // cookie name form is gated_unlock_<postId>_<blockId>=1
    for (let raw of cookies) {
      const cookie = raw.trim();
      if (cookie.startsWith('gated_unlock_') && cookie.includes(`_${blockId}=`)) {
        // Extract between prefix and _<blockId>
        const prefix = 'gated_unlock_';
        const start = prefix.length;
        const end = cookie.indexOf(`_${blockId}=`);
        if (end > start) {
          return cookie.substring(start, end);
        }
      }
    }
    return null;
  }

  /**
   * Set unlock cookie
   */
  setUnlockCookie(postId, blockId, days) {
    const name = `gated_unlock_${postId}_${blockId}`;
    const value = '1';
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }

  /**
   * Check if unlock cookie exists
   */
  hasUnlockCookie(postId, blockId) {
    const name = `gated_unlock_${postId}_${blockId}=`;
    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(name) === 0) {
        return true;
      }
    }
    return false;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new GatedContent());
} else {
  new GatedContent();
}

export default GatedContent;
